// usePatientData.js - Simplified version
import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateIfDate } from "../../classes/utils/DateUtils";
import { GET_PATIENT_INFORMATION_BY_ID_DASHBOARD_QUERY } from "../../queries/patientQueries";
import { ROUTES } from "../../classes/constants/routeConstants";

export function usePatientData(patientId) {
  const [patientData, setPatientData] = useState(null);
  const navigate = useNavigate();

  const { loading, error, refetch } = useQuery(
    GET_PATIENT_INFORMATION_BY_ID_DASHBOARD_QUERY,
    {
      variables: { id: patientId },
      skip: !patientId,
      fetchPolicy: "network-only",
      onCompleted: data => {
        if (data?.patientById) {
          setPatientData(data.patientById);
        } else if (patientId) {
          // Patient ID provided but no data returned - patient doesn't exist
          navigate(ROUTES.NOT_FOUND, { replace: true });
        }
      },
      onError: error => {
        // GraphQL error or network error when fetching patient
        // If patient doesn't exist, redirect to not found page
        if (error.message?.includes("not found") || error.message?.includes("Not found")) {
          navigate(ROUTES.NOT_FOUND, { replace: true });
        }
      },
    }
  );

  const debugRefetch = useCallback(async () => {
    try {
      const result = await refetch();
      // MANUALLY update the state since onCompleted won't be called
      if (result.data?.patientById) {
        setPatientData(result.data.patientById);
      }

      return result;
    } catch (error) {
      throw error;
    }
  }, [refetch, patientId]);

  const formatPersonalData = useCallback(rawData => {
    if (!rawData) return null;
    return {
      name: `${rawData.first_name || ""} ${rawData.last_name || ""}`.trim(),
      dateOfBirth: formatDateIfDate(rawData.birthday) || "-",
      age: rawData.age != null ? rawData.age.toString() : "-",
      sex: rawData.gender || "-",
      nationality: rawData.nationality || "-",
      address: rawData.address
        ? `${rawData.address.street_number || ""}, ${
            rawData.address.street || ""
          }, ${rawData.address.city_code || ""}`
        : "-",
      city: rawData.address ? rawData.address.city || "-" : "-",
      country: rawData.address ? rawData.address.country || "-" : "-",
      email: rawData.email || "-",
      phone: rawData.phone || "-",
      studies: rawData.studies || null,
      spinesim_studies_ids: rawData.spinesim_studies_ids || null,
      notes: rawData.notes ? `${rawData.notes.join(", ")}` : "-",
    };
  }, []);

  const getPersonalData = useCallback(async () => {
    if (!patientId) {
      return { raw: null, formatted: null };
    }
    try {
      const { data } = await refetch();
      const newPatientData = data?.patientById;
      return {
        raw: newPatientData,
        formatted: formatPersonalData(newPatientData),
      };
    } catch (error) {
      return { raw: null, formatted: null };
    }
  }, [patientId, refetch, formatPersonalData]);

  useEffect(() => {
    setPatientData(null);
  }, [patientId]);

  return {
    patientData,
    loading,
    error,
    getPersonalData,
    formatPersonalData,
    refetch: debugRefetch,
  };
}
